/**
 * components/ChatPanel.jsx
 * -------------------------
 * Fixed sidebar AI chat panel — "Cy" the collection assistant.
 *
 * Behavior:
 *   - Rendered by AppHeader when chatbot_enabled=true in settings and user clicks 💬.
 *   - Maintains local message history (role: "user" | "assistant").
 *   - POST /chat/ sends current message + full conversation history to the backend.
 *   - Enter key (without Shift) submits; Shift+Enter inserts a newline.
 *   - Scrolls to the bottom after each new message via bottomRef.
 *
 * Props:
 *   onClose()  — callback to close/hide the panel (sets chatOpen=false in AppHeader).
 */
import React, { useState, useRef, useEffect } from "react";
import api from "../api/api";
import "./ChatPanel.css";
import PropTypes from "prop-types";

export default function ChatPanel({ onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Cy is on the mound... what's your question?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Kiosk scripted Q&A — types question then shows pre-written answer
  useEffect(() => {
    const handler = (e) => {
      const { q, a } = e.detail;
      setMessages(prev => [...prev, { role: "user", text: q }]);
      setTimeout(() => {
        setMessages(prev => [...prev, { role: "assistant", text: a }]);
      }, 1500);
    };
    window.addEventListener("kiosk-chat-qa", handler);
    return () => window.removeEventListener("kiosk-chat-qa", handler);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    const updatedMessages = [...messages, { role: "user", text }];
    setMessages(updatedMessages);
    setLoading(true);
    try {
      const history = updatedMessages.slice(0, -1); // all but the current message
      const res = await api.post("/chat/", { message: text, history });
      setMessages((prev) => [...prev, { role: "assistant", text: res.data.response }]);
      window.dispatchEvent(new Event("collection-changed"));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      setMessages((prev) => [...prev, { role: "assistant", text: `Error: ${detail}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="chat-panel">
      <div className="chat-panel-header">
        <span>Cy</span>
        <button className="chat-close-btn" onClick={onClose}>✕</button>
      </div>

      <div className="chat-messages">
        {messages.map((m, i) => (  // NOSONAR
          <div key={i} className={`chat-bubble ${m.role}`}>
            {m.text}
          </div>
        ))}
        {loading && <div className="chat-bubble assistant">Here's the windup...</div>}
        <div ref={bottomRef} />
      </div>

      <div className="chat-input-row">
        <textarea
          className="chat-input"
          rows={2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Ask Cy about your collection..."
        />
        <button className="chat-send-btn" onClick={send} disabled={loading}>
          Send
        </button>
      </div>
    </div>
  );
}

ChatPanel.propTypes = {
  onClose: PropTypes.func,
};
